import { Component, effect, inject, signal } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatMenuModule } from '@angular/material/menu';
import { TranslateService } from '@ngx-translate/core';
import { debounceTime } from 'rxjs';

import { AssignmentsService } from '@module-instance/assignments/common';
import {
  FilterType,
  PrimaryUser,
  SyncPrimaryUsersModel,
} from '@module-instance/assignments/primary-users-dialog/primary-user-dialog.model';
import { CipoSelectComponent } from '@shared/components/fields/cipo-select/cipo-select.component';
import { CipoFieldTypes, CipoListControl } from '@shared/components/fields/common';
import { DEBOUNCE_TIMES } from '@shared/consts';
import { CipoDialogHeaderComponent } from '@shared/dialogs';
import { CipoDialogModule, CipoFormModule } from '@shared/modules';
import { NotificationService } from '@shared/services';
import { DictionaryStore } from '@signal-store';

@Component({
  selector: 'app-primary-users-dialog',
  imports: [
    MatExpansionModule,
    CipoSelectComponent,
    CipoDialogModule,
    MatMenuModule,
    CipoFormModule,
    CipoDialogHeaderComponent,
  ],
  templateUrl: './primary-users-dialog.component.html',
})
export class PrimaryUsersDialogComponent {
  translate = inject(TranslateService);
  assignmentsService = inject(AssignmentsService);
  notification = inject(NotificationService);
  private dictionaryStore = inject(DictionaryStore);
  readonly dialogRef = inject(MatDialogRef<PrimaryUsersDialogComponent>);
  readonly _entityInstanceId = inject<number>(MAT_DIALOG_DATA);

  saving = signal(false);
  searchControl = new FormControl('');
  FILTER_TYPE = FilterType;
  currentFilterType = signal(FilterType.All);
  entityInstanceId: number;
  roles: PrimaryUser[] = [];
  initialRoles: PrimaryUser[] = [];
  arrayControls: Record<number, CipoListControl> = {};

  constructor() {
    effect(() => {
      const saving = this.saving();

      Object.values(this.arrayControls).forEach(control => {
        if (saving) {
          control.disable({ emitEvent: false });
        } else {
          control.enable({ emitEvent: false });
        }
      });
    });

    this.entityInstanceId = this._entityInstanceId;
    this.searchControl.valueChanges
      .pipe(debounceTime(DEBOUNCE_TIMES.default))
      .subscribe(value => this.search(value, this.currentFilterType()));

    this.loadRoles();
  }

  loadRoles() {
    this.arrayControls = [];

    this.dictionaryStore.getPrimaryUsers({ contractId: this.entityInstanceId }).subscribe(primaryUsers => {
      this.roles = primaryUsers;
      this.initialRoles = primaryUsers;

      for (let index = 0; index < primaryUsers.length; index++) {
        const control = new CipoListControl(primaryUsers[index].userId, {
          label: 'assignments.primaryUser',
          multiple: false,
          type: CipoFieldTypes.Select,
          options: primaryUsers[index].users,
          noneLabel: 'cipoFields.none',
        });

        this.arrayControls[primaryUsers[index].roleId] = control;
      }
    });
  }

  search(criteria: string, filterType: FilterType) {
    this.currentFilterType.set(filterType);

    if (!criteria && filterType == FilterType.All) {
      this.roles = this.initialRoles;
      return;
    }

    // first set new chnages in userId so we filter with real data
    this.setUserId(this.initialRoles);
    this.roles = this.initialRoles.filter(
      row =>
        (!criteria || (criteria && row.roleName.toString().toLowerCase().includes(criteria.toLowerCase()))) &&
        (filterType == FilterType.All ||
          (filterType == FilterType.Assigned && row.userId !== 0) ||
          (filterType == FilterType.NotAssigned && row.userId === 0)),
    );
  }

  clearInput() {
    this.searchControl.reset();
  }

  savePrimaryUsers() {
    if (this.roles) {
      this.saving.set(true);
      this.setUserId();

      const syncModel: SyncPrimaryUsersModel = {
        contractId: this.entityInstanceId,
        primaries: this.roles,
      };

      this.assignmentsService.savePrimaryUsers(syncModel).subscribe({
        complete: () => {
          this.notification.success('assignments.successfully');
          this.saving.set(false);
          this.dialogRef.close(true);
        },
        error: () => {
          this.saving.set(false);
        },
      });
    }
  }

  setUserId(list?: PrimaryUser[]) {
    list = list ?? this.roles;
    if (list?.length) {
      for (let index = 0; index < list.length; index++) {
        list[index].userId = this.arrayControls[list[index].roleId]?.value;
      }
    }
  }
}
